import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Loader2, KeyRound, Lock } from "lucide-react";

// Item 1: Password reset via email OTP (replaces the old reset-token flow).
export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1: enter email, 2: enter OTP+new password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => { if (cooldown <= 0) return; const t = setTimeout(() => setCooldown((c) => c - 1), 1000); return () => clearTimeout(t); }, [cooldown]);

  const sendOtp = async (e) => {
    e?.preventDefault(); setLoading(true);
    try {
      await api.post("/auth/forgot-password-otp", { email });
      toast.success("If the email is registered, a code has been sent.");
      setStep(2); setCooldown(60);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setLoading(false); }
  };

  const resetNow = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/auth/reset-password-otp", { email, otp, new_password: newPassword });
      toast.success("Password updated — please log in.");
      nav("/login");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Reset failed"); }
    finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="card-dark p-8">
          <div className="chip bg-lime/15 text-lime mb-4">Reset Password</div>
          {step === 1 ? (
            <>
              <h1 className="font-display uppercase text-3xl md:text-4xl leading-[0.95]">Forgot your password?</h1>
              <p className="mt-2 text-sm text-text/70">Enter your email — we'll send a 6-digit code.</p>
              <form onSubmit={sendOtp} className="mt-6 space-y-3">
                <input required type="email" data-testid="fp-email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark" />
                <button data-testid="fp-send-otp" disabled={loading || !email} className="btn-primary w-full justify-center">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} {loading ? "Sending..." : "Send Code"}
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-display uppercase text-3xl md:text-4xl leading-[0.95]">Enter your code.</h1>
              <p className="mt-2 text-sm text-text/70">Sent to <span className="text-lime font-mono">{email}</span> — expires in 5 minutes.</p>
              <form onSubmit={resetNow} className="mt-6 space-y-3">
                <input required inputMode="numeric" pattern="\d{6}" maxLength={6} data-testid="fp-otp" placeholder="6-digit code" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g,"").slice(0,6))} className="input-dark text-center text-2xl tracking-[0.5em] font-display" />
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                  <input required type="password" minLength={6} data-testid="fp-new-password" placeholder="New password (min 6 chars)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="input-dark !pl-10" />
                </div>
                <button data-testid="fp-submit" disabled={loading || otp.length !== 6 || newPassword.length < 6} className="btn-lime w-full justify-center">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} {loading ? "Updating..." : "Reset Password"}
                </button>
              </form>
              <div className="mt-4 flex items-center justify-between text-xs font-mono">
                <button onClick={sendOtp} disabled={cooldown > 0 || loading} data-testid="fp-resend" className="text-lime disabled:text-muted underline">
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                </button>
                <button onClick={() => setStep(1)} className="text-muted underline">Change email</button>
              </div>
            </>
          )}
          <div className="mt-6 text-xs font-mono text-muted">
            Remembered it? <Link to="/login" className="text-lime underline font-bold">Log in</Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
